import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUserDetailsMutation } from '@src/generated/graphql';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import i18n from '@src/i18n';
import { logError } from '@src/services/error-logger';
import DropdownMenu from '../DropdownMenu';

// Flags taken from https://github.com/catamphetamine/country-flag-icons

import PtFlag from './flags/pt.svg?react';
import UsFlag from './flags/us.svg?react';

interface Props {
  className?: string;
}

interface LanguageConfig {
  code: string;
  label: string;
  flag: React.FC<React.SVGProps<SVGSVGElement> & { title?: string }>;
}

const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: 'en', label: 'English', flag: UsFlag },
  { code: 'pt', label: 'Português', flag: PtFlag },
];

const DEFAULT_LANGUAGE_CONFIG = SUPPORTED_LANGUAGES[0];

// we want to always show the language switcher if the language
// is ever portuguese. But if they switch to English, we keep it
// in English as part of the i18n deprecation process.
let hasShownLanguageSwitcher = false;

function LanguageSwitcher({ className }: Props): JSX.Element | null {
  const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [updateUserDetails] = useUpdateUserDetailsMutation();

  const userLocale = user?.locale;

  useEffect(() => {
    const onLanguageChanged = (lng: string): void => {
      if (userLocale !== lng) {
        updateUserDetails({ variables: { input: { locale: lng } } }).catch(
          (err) => logError(err),
        );
      }

      setCurrentLanguage(lng);
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [updateUserDetails, userLocale]);

  const currentConfig =
    SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) ||
    DEFAULT_LANGUAGE_CONFIG;

  if (currentLanguage === 'en' && !hasShownLanguageSwitcher) {
    return null;
  }
  hasShownLanguageSwitcher = true;

  return (
    <DropdownMenu className={className}>
      <DropdownMenu.Button className="flex rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
        <span className="sr-only">
          {t('app.open_language_menu_sr', 'Open language menu')}
        </span>
        <currentConfig.flag
          title={`${currentConfig.label} flag`}
          className="h-5 w-5"
        />
      </DropdownMenu.Button>
      <DropdownMenu.Items>
        {SUPPORTED_LANGUAGES.map((config) => (
          <DropdownMenu.ButtonItem
            onClick={() => i18n.changeLanguage(config.code)}
            key={config.code}
          >
            <div className="flex items-center space-x-2">
              <config.flag title={`${config.label} flag`} className="h-5 w-5" />
              <div>{config.label}</div>
            </div>
          </DropdownMenu.ButtonItem>
        ))}
      </DropdownMenu.Items>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
